import {RefObject, useEffect, useRef} from 'react';
import {injectHtmlSlotFallbacks} from '../components/element/types/text/htmlSlotFallbacks';
import {IElementHtml} from '../incase-domain/elements/html/html';
import {IRunFile} from '../incase-domain/proptypes/runfile';
import {presCustomVarsParser} from '../util/slide.utils';
import {useLayout} from './useLayout';

interface UseHtmlResult {
    containerRef: RefObject<HTMLDivElement>;
}

export function useHtmlIframe(
    element: IElementHtml,
    runfile: IRunFile,
    onClick?: () => void,
    onReady?: (doc: Document) => void
): UseHtmlResult {
    const {text} = element;
    const {mobileView} = useLayout();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const onClickRef = useRef(onClick);
    const onReadyRef = useRef(onReady);
    onClickRef.current = onClick;
    onReadyRef.current = onReady;

    useEffect(() => {
        if (!text || !containerRef.current) return undefined;

        if (!iframeRef.current) {
            const iframe = document.createElement('iframe');
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.border = 'none';
            iframe.style.display = 'block';
            iframe.style.overflow = 'auto';

            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(iframe);
            iframeRef.current = iframe;
        }

        const parsedText = presCustomVarsParser(text, runfile);

        const iframe = iframeRef.current;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

        if (iframeDoc) {
            const container = containerRef.current;
            container.dataset.htmlLoaded = 'false';

            let settled = false;
            const markLoaded = (): void => {
                if (settled || !containerRef.current) return;
                settled = true;
                containerRef.current.dataset.htmlLoaded = 'true';
            };

            const afterFonts = (): void => {
                Promise.resolve(iframe.contentDocument?.fonts?.ready)
                    .catch(() => undefined)
                    .finally(markLoaded);
            };

            // document.write can race with the initial about:blank load; settle from either path.
            iframe.addEventListener('load', afterFonts, {once: true});

            iframeDoc.open();
            iframeDoc.write(parsedText);
            iframeDoc.close();
            if (iframeDoc.body) {
                iframeDoc.body.style.margin = '0';
            }
            injectHtmlSlotFallbacks(iframeDoc);
            queueMicrotask(afterFonts);
            const fallbackTimer = window.setTimeout(markLoaded, 1500);

            const handleClick = (event: Event): void => {
                const view = iframeDoc.defaultView;
                const {target} = event;
                let clicked: Element | null = null;
                if (view && target instanceof view.Element) {
                    clicked = target;
                } else if (view && target instanceof view.Node) {
                    clicked = target.parentElement;
                }
                if (clicked?.closest('[data-incase-slot]')) return;
                onClickRef.current?.();
            };
            iframeDoc.addEventListener('click', handleClick);
            onReadyRef.current?.(iframeDoc);

            return () => {
                window.clearTimeout(fallbackTimer);
                iframeDoc.removeEventListener('click', handleClick);
            };
        }

        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, element.coverSlide]);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (element.coverSlide) {
            if (iframe) iframe.style.height = '100%';
            return undefined;
        }
        if (iframe && mobileView) {
            iframe.addEventListener('load', () => {
                if (iframe.contentWindow) {
                    iframe.style.height = `${iframe.contentWindow.document.body.scrollHeight}px`;
                }
            });

            const resize = new ResizeObserver(() => {
                if (iframe.contentWindow) {
                    iframe.style.height = `${iframe.contentWindow.document.body.scrollHeight}px`;
                }
            });
            resize.observe(document.body);
        }
        return undefined;
    }, [mobileView, element.coverSlide]);

    return {containerRef};
}
